import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useAdminLearnerAttendanceHistory } from '@/hooks/admin/useAdminLearnerAttendanceHistory'
import { AttendanceHistoryList, buildAttendanceHistoryLabels } from '@/components/attendance/AttendanceHistoryList'
import { useAttendanceGrowthAlert } from '@/hooks/coaching/useAttendanceGrowthAlert'

interface Props {
  userId: number
  learnerName: string
  onClose: () => void
}

export function AttendanceHistoryModal({ userId, learnerName, onClose }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAdminLearnerAttendanceHistory(userId, true)

  const items = data?.pages.flatMap((p) => p.items) ?? []

  const labels = buildAttendanceHistoryLabels(t)

  const { renderActions, dialog } = useAttendanceGrowthAlert(userId)

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('admin:students.attendanceHistory.title', { name: learnerName })}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label={t('common:button.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto">
          {isLoading && <p className="py-6 text-center text-sm text-gray-500">{t('common:label.loading')}</p>}
          {isError && <p className="py-6 text-center text-sm text-gray-600">{t('admin:students.attendanceHistory.error')}</p>}
          {!isLoading && !isError && items.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-600">{t('admin:students.attendanceHistory.empty')}</p>
          )}
          {items.length > 0 && (
            <>
              <AttendanceHistoryList items={items} labels={labels} renderActions={renderActions} />
              {hasNextPage && (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="mt-4 w-full px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm"
                >
                  {t('admin:students.attendanceHistory.loadMore')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    {dialog}
    </>
  )
}
